import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import Modal from 'modules/views/shared/Modal';
import TextField from 'modules/views/shared/TextField';
import Select from 'modules/views/shared/Select';
import DatePicker from 'modules/views/shared/DatePicker';
import AddImages from 'modules/views/shared/AddImages';

import { IFile } from 'models/general';
import { IRequiredCertificationGroup } from 'models/certifications';
import { useStylesAddCertificationModal } from '../styles';
import { useAppSelector } from 'modules/state-mgmt/store';
import { actionMapForOtherLoaders } from 'constants/actions';
import { CertificationType, TypeOption, CertificationFormData } from '../types';
import { createGuid } from 'utils';
import { getImageFormat } from 'utils/files';

interface IAddCertificationModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSave: (certificationData: CertificationFormData) => void;
  typeOptions: TypeOption[];
  editData?: CertificationFormData | null;
  isEditMode?: boolean;
  isLoadingEditDetails?: boolean;
  isMyCertifications?: boolean; // Flag to determine which actions to use
  preselectedCertificateId?: string | null; // ID of certificate to preselect
  disableCertificateTypeSelection?: boolean; // Flag to disable certificate type selection
  certificationGroups?: IRequiredCertificationGroup[]; // Certification groups for required certifications
  showGroupSelection?: boolean; // Flag to show/hide group selection (default true)
}

const INIT_GROUP_CONFIG = {
  isCertificationIdRequired: false,
  isDescriptionRequired: false,
  isExpirationDateRequired: false,
  isMediaRequired: false,
};

const AddCertificationModal = ({
  isVisible,
  onClose,
  onSave,
  typeOptions,
  editData,
  isEditMode = false,
  isLoadingEditDetails = false,
  isMyCertifications = false,
  preselectedCertificateId = null,
  disableCertificateTypeSelection = false,
  certificationGroups = undefined,
  showGroupSelection = true,
}: IAddCertificationModalProps) => {
  const classes = useStylesAddCertificationModal();
  const { t } = useTranslation();
  const { isAddingCertificate, isUpdatingCertificate } = useAppSelector(state => ({
    isAddingCertificate:
      state.loader.otherLoaders[
        isMyCertifications ? actionMapForOtherLoaders.addMyCertification : actionMapForOtherLoaders.addCertification
      ]?.isLoading || false,
    isUpdatingCertificate:
      state.loader.otherLoaders[
        isMyCertifications
          ? actionMapForOtherLoaders.updateMyCertification
          : actionMapForOtherLoaders.updateCertification
      ]?.isLoading || false,
  }));

  const isProcessing = isAddingCertificate || isUpdatingCertificate;

  const [formData, setFormData] = useState<CertificationFormData>({
    type: '',
    certificationId: '',
    description: '',
    issueDate: null,
    expirationDate: null,
    images: [],
  });

  const [errors, setErrors] = useState<Partial<Record<keyof CertificationFormData, string>>>({});
  const [deletedFileIds, setDeletedFileIds] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<{ value: string; label: string } | ''>('');
  const [selectedGroupConfig, setSelectedGroupConfig] = useState(INIT_GROUP_CONFIG);

  useEffect(() => {
    if (!isVisible) {
      setFormData({
        type: '',
        certificationId: '',
        description: '',
        issueDate: null,
        expirationDate: null,
        images: [],
      });
      setErrors({});
      setDeletedFileIds([]);
      setSelectedGroup('');
      setSelectedGroupConfig(INIT_GROUP_CONFIG);
      return;
    }
    if (isEditMode && editData) {
      const selectedOption = editData.certificationTypeId
        ? typeOptions.find(option => option.value.id === editData.certificationTypeId)
        : null;
      setFormData({ ...editData, type: selectedOption ?? '' });
      setErrors({});
      setDeletedFileIds([]);
    } else if (!isEditMode) {
      const foundOption = preselectedCertificateId
        ? typeOptions.find(option => option.value.id === preselectedCertificateId)
        : undefined;
      setFormData({
        type: foundOption ?? '',
        certificationId: '',
        description: '',
        issueDate: null,
        expirationDate: null,
        images: [],
      });
      setErrors({});
      setDeletedFileIds([]);
    }
  }, [isVisible, isEditMode, editData, typeOptions, preselectedCertificateId]);

  useEffect(() => {
    if (certificationGroups && certificationGroups.length > 0) {
      let selectedGroupData = null;
      if (showGroupSelection) {
        selectedGroupData = certificationGroups.find(
          g => g.id === (typeof selectedGroup === 'object' ? selectedGroup.value : selectedGroup),
        );
      } else {
        selectedGroupData = certificationGroups.find(group =>
          group.certifications.some((cert: any) => {
            const certId =
              typeof formData.type === 'object' && 'value' in formData.type ? formData.type.value.id : null;
            return cert.id === certId;
          }),
        );
      }
      setSelectedGroupConfig({
        isCertificationIdRequired: selectedGroupData?.isCertificationIdRequired || false,
        isDescriptionRequired: selectedGroupData?.isDescriptionRequired || false,
        isExpirationDateRequired: selectedGroupData?.isExpirationDateRequired || false,
        isMediaRequired: selectedGroupData?.isMediaRequired || false,
      });
      // remove all errors when group changes to avoid confusion for users
      setErrors({});
    }
  }, [selectedGroup, formData.type, certificationGroups, showGroupSelection]);

  const selectedCertType =
    formData.type && typeof formData.type === 'object' && 'value' in formData.type ? formData.type.value : null;

  const validateForm = () => {
    const newErrors: Partial<Record<keyof CertificationFormData, string>> = {};

    if (!formData.type) {
      newErrors.type = t('certifications.addModal.typeRequired');
    }
    if (!formData.issueDate) {
      newErrors.issueDate = t('certifications.addModal.issueDateRequired');
    }
    if (selectedGroupConfig.isCertificationIdRequired && !formData.certificationId?.trim()) {
      newErrors.certificationId = t('certifications.addModal.certificationIdRequired');
    }
    if (selectedGroupConfig.isDescriptionRequired && !formData.description?.trim()) {
      newErrors.description = t('certifications.addModal.descriptionRequired');
    }
    if (selectedGroupConfig.isMediaRequired && formData.images.filter(img => !img.isDeleted).length === 0) {
      newErrors.images = t('certifications.addModal.mediaRequired');
    }
    if (
      !formData.expirationDate &&
      (selectedGroupConfig.isExpirationDateRequired ||
        (selectedCertType &&
          typeof selectedCertType === 'object' &&
          'isExpirationDateMandatory' in selectedCertType &&
          (selectedCertType as CertificationType).isExpirationDateMandatory))
    ) {
      newErrors.expirationDate = t('certifications.addModal.expirationDateRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Create group options from certificationGroups
  const groupOptions = React.useMemo(() => {
    if (!certificationGroups || certificationGroups.length === 0) {
      return [];
    }
    return certificationGroups.map(group => ({
      value: group.id,
      label: group.name,
    }));
  }, [certificationGroups]);

  // Filter typeOptions based on selected group
  const filteredTypeOptions = React.useMemo(() => {
    if (!certificationGroups || !selectedGroup) {
      return typeOptions;
    }

    const selectedGroupData = certificationGroups.find(
      g => g.id === (typeof selectedGroup === 'object' ? selectedGroup.value : selectedGroup),
    );

    if (!selectedGroupData) {
      return typeOptions;
    }

    // Get certification IDs from the selected group
    const groupCertificationIds = selectedGroupData.certifications.map(cert => cert.id);

    // Filter typeOptions to only include certifications from the selected group
    return typeOptions.filter(option => groupCertificationIds.includes(option.value.id));
  }, [certificationGroups, selectedGroup, typeOptions]);

  const handleInputChange = (field: keyof CertificationFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleGroupChange = (value: any) => {
    setSelectedGroup(value);
    // Reset type selection when group changes
    setFormData(prev => ({
      ...prev,
      type: '',
    }));
    // Clear type error
    if (errors.type) {
      setErrors(prev => ({
        ...prev,
        type: undefined,
      }));
    }
  };

  const handleAddImage = useCallback((imageSrc: string | string[]) => {
    // Keep base64 in `image` (not `url`) and set `fileName` with the correct extension.
    // AddImages detects PDF format from the base64 data URL when `url` is absent — setting
    // `url` to the base64 string diverts detection to the `displayName` branch and breaks PDF previews.
    const srcs = Array.isArray(imageSrc) ? imageSrc : [imageSrc];
    const newFiles: IFile[] = srcs.map(src => {
      const tempId = createGuid();
      return { tempId, image: src, fileName: `${tempId}.${getImageFormat(src)}` };
    });
    setFormData(prev => ({ ...prev, images: [...prev.images, ...newFiles] }));
    setErrors(prev => ({ ...prev, images: undefined }));
  }, []);

  const handleDeleteImage = useCallback((file: IFile) => {
    // Track existing files (ones with server IDs) for deletion
    if (file.id) {
      setDeletedFileIds(prev => [...prev, file.id!]);
    }

    // Remove the file from the images array
    setFormData(prev => ({
      ...prev,
      images: prev.images
        .map(img => {
          if (file.id && img.id === file.id) {
            return { ...img, isDeleted: true };
          }
          return img;
        })
        .filter(img => {
          // Keep the image if it has an ID (it's now marked isDeleted)
          if (img.id) return true;
          // If it's a temp file (no ID), filter it out if it matches the deletion target
          if (file.tempId) return img.tempId !== file.tempId;
          return true;
        }),
    }));
  }, []);

  const handleCancel = () => {
    if (isProcessing) return; // Prevent closing while loading
    onClose();
  };

  const handleSave = () => {
    if (isProcessing) return; // Prevent multiple submissions

    if (validateForm()) {
      onSave({ ...formData, deletedFileIds });
      // Don't reset form or close modal here - let parent handle it after async action completes
    }
  };

  return (
    <Modal
      title={isEditMode ? t('certifications.editModal.title') : t('certifications.addModal.title')}
      isVisible={isVisible}
      modalStyleClass={classes.modal}
      modalContentStyleClass={classes.modalContent}
      disableScrollLock={true}
      testId="add-cert-modal"
      Content={
        <div className={classes.content}>
          {certificationGroups && certificationGroups.length > 0 && showGroupSelection && (
            <div className={classes.fieldContainer}>
              <Select
                inputLabel={t('certifications.addModal.certificationGroup')}
                required={true}
                options={groupOptions}
                selectedValue={selectedGroup}
                onChange={handleGroupChange}
                testId="add-cert-group-select"
              />
            </div>
          )}

          <div className={classes.formGrid}>
            <div className={classes.fieldContainer}>
              <Select
                inputLabel={t('certifications.addModal.type')}
                required={true}
                options={showGroupSelection ? filteredTypeOptions : typeOptions}
                selectedValue={formData.type}
                error={errors.type}
                grouped={true}
                onChange={value => handleInputChange('type', value)}
                disabled={
                  disableCertificateTypeSelection ||
                  (certificationGroups && certificationGroups.length > 0 && showGroupSelection && !selectedGroup)
                }
                testId="add-cert-type-select"
              />
            </div>

            <div className={classes.fieldContainer}>
              <TextField
                label={t('certifications.addModal.certificationId')}
                placeholder={t('certifications.addModal.certificationIdPlaceholder')}
                required={selectedGroupConfig.isCertificationIdRequired}
                value={formData.certificationId}
                error={errors.certificationId}
                testId="certificationId-input"
                onChange={e => handleInputChange('certificationId', e.target.value)}
              />
            </div>
          </div>

          <div className={classes.fieldContainer}>
            <TextField
              label={t('certifications.addModal.description')}
              placeholder={t('certifications.addModal.descriptionPlaceholder')}
              required={selectedGroupConfig.isDescriptionRequired}
              value={formData.description}
              error={errors.description}
              multiline={true}
              rows={5}
              inputProps={{ maxLength: 255 }}
              testId="certDescription-input"
              onChange={e => handleInputChange('description', e.target.value)}
            />
            <span className={classes.charCount}>{formData.description.length}/255</span>
          </div>

          <div className={classes.formGrid}>
            <div className={classes.fieldContainer}>
              <DatePicker
                inputLabel={t('certifications.addModal.issueDate')}
                required={true}
                value={formData.issueDate}
                error={errors.issueDate}
                maxDate={moment()}
                testId="add-cert-issue-date-picker"
                onChange={value => handleInputChange('issueDate', value)}
              />
            </div>

            <div className={classes.fieldContainer}>
              <DatePicker
                inputLabel={t('certifications.addModal.expirationDate')}
                required={
                  selectedGroupConfig.isExpirationDateRequired ||
                  (selectedCertType &&
                    typeof selectedCertType === 'object' &&
                    'isExpirationDateMandatory' in selectedCertType &&
                    (selectedCertType as CertificationType).isExpirationDateMandatory)
                }
                value={formData.expirationDate}
                error={errors.expirationDate}
                minDate={moment()}
                testId="add-cert-expiration-date-picker"
                onChange={value => handleInputChange('expirationDate', value)}
              />
            </div>
          </div>

          <div className={classes.addImageSection}>
            <AddImages
              images={formData.images.filter(img => !img.isDeleted)}
              onCapture={handleAddImage}
              onDelete={handleDeleteImage}
              containerStyleClass={classes.addImageContainer}
              textContainerStyleClass={classes.addImageTextContainer}
              maxImages={5}
              isLoadingImages={isLoadingEditDetails && isEditMode}
              multiple={true}
              error={errors.images}
              testId="add-cert-image"
            />
          </div>
        </div>
      }
      actionButtons={[
        {
          label: t('common.cancel'),
          variant: 'outlined',
          styleClass: classes.cancelButton,
          onClick: handleCancel,
          disabled: isProcessing,
          testId: 'add-cert-cancel-button',
        },
        {
          label: isEditMode ? t('common.update') : t('common.save'),
          styleClass: classes.saveButton,
          onClick: handleSave,
          isLoading: isProcessing,
          disabled: isProcessing,
          testId: 'add-cert-save-button',
        },
      ]}
      onClose={isProcessing ? undefined : onClose}
    />
  );
};

export default React.memo(AddCertificationModal);
